import PropTypes from 'prop-types'
import { CircularProgress, Box, Typography } from '@mui/material'
import React from "react"
import Colors from "../../utils/Colors"

/**
 * LoadingComponent is a component that displays a loading spinner and a text message.
 *
 * @param {Object} props The props for the component.
 * @param {number} props.progressSize The size of the loading spinner.
 * @param {string} props.progressColor The color of the loading spinner.
 * @param {string} props.loadingText The text to display with the spinner.
 * @param {string} props.textColor The color of the text.
 * @param {string} props.textSize The font size of the text.
 * @param {string} props.textMargin The margin between the text and the spinner.
 * @param {string} props.layoutOrientation The layout orientation. It can be 'vertical' or 'horizontal'.
 * @param {string} props.minHeight The minimum height of the component.
 * @param {string} props.fontWeight The font weight of the text.
 * @param {string} props.alignItems The alignment of the component.
 * @param {string} props.justifyContent The alignment of the component.
 */
const LoadingComponent = (
  {
    progressSize,
    progressColor,
    loadingText,
    textColor,
    textSize,
    textMargin,
    layoutOrientation,
    minHeight,
    fontWeight,
    alignItems,
    justifyContent,
  },
) => {
  const isVertical = layoutOrientation === 'vertical'

  const boxStyles = {
    display: 'flex',
    alignItems: { alignItems },
    justifyContent: { justifyContent },
    minHeight: minHeight,
    flexDirection: isVertical ? 'column' : 'row',
    width: '100%',
  }

  const progressStyles = isVertical ?
    { marginBottom: textMargin, color: progressColor } :
    { marginRight: textMargin, color: progressColor }

  const textStyles = {
    color: textColor,
    fontSize: textSize,
    fontWeight: fontWeight,
  }

  return (
    <Box sx={boxStyles}>
      <CircularProgress size={progressSize} sx={progressStyles} />
      <Typography variant="body1" sx={textStyles}>
        {loadingText}
      </Typography>
    </Box>
  )
}

LoadingComponent.propTypes = {
  progressSize: PropTypes.number,
  progressColor: PropTypes.string,
  loadingText: PropTypes.string,
  textColor: PropTypes.string,
  textSize: PropTypes.string,
  textMargin: PropTypes.string,
  layoutOrientation: PropTypes.oneOf(['vertical', 'horizontal']),
  minHeight: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  alignItems: PropTypes.string,
  justifyContent: PropTypes.string,
}

LoadingComponent.defaultProps = {
  progressSize: 36,
  progressColor: '#fff',
  loadingText: 'Carregando...',
  textColor: '#fff',
  textSize: '16px',
  textMargin: '16px',
  layoutOrientation: 'vertical',
  minHeight: '70vh',
  fontWeight: 'normal',
  alignItems: 'center',
  justifyContent: 'center',
}

export const horizontalLeftAlignedLoadingProps = {
  layoutOrientation: "horizontal",
  textColor: Colors.primaryColor,
  progressSize: 24,
  progressColor: Colors.primaryColor,
  minHeight: 'auto',
  fontWeight: '700',
  width: 'auto',
  alignItems: 'left',
  justifyContent: 'left',
}

export const smallLoadingWithoutTextProps = {
  layoutOrientation: "vertical",
  textColor: Colors.primaryColor,
  progressSize: 24,
  progressColor: Colors.primaryColor,
  minHeight: 'auto',
  fontWeight: '700',
  width: 'auto',
  textMargin: '0',
  alignItems: 'center',
  justifyContent: 'center',
  loadingText: '',
}

export default LoadingComponent
